import { formatNumber } from "../../../utils";
import {
  BdcBranch,
  BdcDashboardGranularity,
  BdcDashboardSeriesPoint,
  BdcDashboardStoreCell,
  BdcDashboardStoreRow,
  BdcLapseLevel,
} from "../../../types/bdcDashboard.type";
import { TagTone } from "../styles";

const TH_MONTH_SHORT = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค.",
];
const TH_MONTH_LONG = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
/** Sunday-first, as `Date.getUTCDay()`. */
const TH_WEEKDAY = ["อาทิตย์", "จันทร์", "อังคาร", "พุธ", "พฤหัสบดี", "ศุกร์", "เสาร์"];
const TH_WEEKDAY_SHORT = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];

interface DateParts {
  year: number;
  month: number;
  day: number;
  weekday: number;
}

/** Split a bare `YYYY-MM-DD` without any time zone shift; the weekday is the calendar's. */
const partsOf = (value: string | null | undefined): DateParts | null => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  return { year, month, day, weekday: new Date(Date.UTC(year, month, day)).getUTCDay() };
};

/** Two-digit Buddhist-era year: 2026 → 69. */
const beShort = (year: number) => String((year + 543) % 100).padStart(2, "0");

/** `2026-10-09` → `9 ต.ค. 69`. */
export const shortDate = (value: string | null | undefined): string => {
  const p = partsOf(value);
  return p ? `${p.day} ${TH_MONTH_SHORT[p.month]} ${beShort(p.year)}` : value ?? "-";
};

/** `2026-10-09` → `9 ต.ค.` (no year — for lists inside one known window). */
export const dayMonth = (value: string | null | undefined): string => {
  const p = partsOf(value);
  return p ? `${p.day} ${TH_MONTH_SHORT[p.month]}` : value ?? "-";
};

/** `2026-10-09` → `ศุกร์ 9 ต.ค. 69`. */
export const weekdayDate = (value: string | null | undefined): string => {
  const p = partsOf(value);
  return p ? `${TH_WEEKDAY[p.weekday]} ${shortDate(value)}` : value ?? "-";
};

/** `2026-10-09` → `ต.ค. 69`. */
export const monthLabel = (value: string | null | undefined): string => {
  const p = partsOf(value);
  return p ? `${TH_MONTH_SHORT[p.month]} ${beShort(p.year)}` : value ?? "-";
};

/** `2026-10-01` → `ตุลาคม 2569`. */
export const monthLongLabel = (value: string | null | undefined): string => {
  const p = partsOf(value);
  return p ? `${TH_MONTH_LONG[p.month]} ${p.year + 543}` : value ?? "-";
};

/**
 * A date range, saying the month / year once when both ends share it:
 * `1–9 ต.ค. 69`, `28 ก.ย. – 4 ต.ค. 69`, `29 ธ.ค. 68 – 4 ม.ค. 69`.
 */
export const rangeLabel = (from: string | null | undefined, to: string | null | undefined): string => {
  const a = partsOf(from);
  const b = partsOf(to);
  if (!a || !b) return `${from ?? "-"} – ${to ?? "-"}`;
  if (from === to) return shortDate(from);
  if (a.year === b.year && a.month === b.month) return `${a.day}–${shortDate(to)}`;
  if (a.year === b.year) return `${dayMonth(from)} – ${shortDate(to)}`;
  return `${shortDate(from)} – ${shortDate(to)}`;
};

/** The selected period as the page heads say it. */
export const periodLabel = (granularity: BdcDashboardGranularity, from: string, to: string): string => {
  if (granularity === "day") return weekdayDate(from);
  if (granularity === "month") return monthLongLabel(from);
  return rangeLabel(from, to);
};

/** What the comparison is against: day vs the same weekday a week earlier. */
export const previousLabel = (granularity: BdcDashboardGranularity, date: string): string => {
  if (granularity === "week") return "สัปดาห์ก่อน";
  if (granularity === "month") return "เดือนก่อน";
  const p = partsOf(date);
  return p ? `${TH_WEEKDAY[p.weekday]}ก่อน` : "สัปดาห์ก่อน";
};

/** `▲ 12.3%` / `▼ 38.5%` / `0.0%`; null (nothing to compare against) → null. */
export const changeText = (pct: number | null | undefined): string | null => {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  const text = `${formatNumber(Math.abs(pct), { decimals: 1 })}%`;
  if (pct > 0) return `▲ ${text}`;
  if (pct < 0) return `▼ ${text}`;
  return text;
};

/** A percent from the API (already 0–100), one decimal; null → `—`. */
export const percentText = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? "—"
    : `${formatNumber(value, { decimals: 1 })}%`;

/** part ÷ whole as a percent; null when whole is 0. */
export const shareOf = (part: number, whole: number): number | null =>
  whole > 0 ? (part / whole) * 100 : null;

/** Pieces ordered but not delivered; never negative. */
export const shortfall = (ordered: number, delivered: number): number =>
  Math.max(0, (ordered ?? 0) - (delivered ?? 0));

/** Axis ticks: 75000 → `75k`, 1200000 → `1.2M`. */
export const compactNumber = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${formatNumber(value / 1_000_000, { decimals: abs % 1_000_000 === 0 ? 0 : 1 })}M`;
  if (abs >= 1_000) return `${formatNumber(value / 1_000, { decimals: abs % 1_000 === 0 ? 0 : 1 })}k`;
  return String(value);
};

export interface SeriesRow extends BdcDashboardSeriesPoint {
  /** x-axis tick. */
  tick: string;
  /** Tooltip heading. */
  title: string;
}

/** Chart rows: day ticks `พฤ 1`, week ticks on the Monday `5 ต.ค.`, month ticks `ต.ค. 69`. */
export const toSeriesRows = (
  series: BdcDashboardSeriesPoint[],
  granularity: BdcDashboardGranularity,
): SeriesRow[] =>
  series.map((point) => {
    const p = partsOf(point.period_start);
    let tick = point.period_start;
    let title = point.period_start;
    if (p && granularity === "day") {
      tick = `${TH_WEEKDAY_SHORT[p.weekday]} ${p.day}`;
      title = weekdayDate(point.period_start);
    } else if (p && granularity === "week") {
      tick = dayMonth(point.period_start);
      title = `สัปดาห์ ${rangeLabel(point.period_start, point.period_end)}`;
    } else if (p) {
      tick = monthLabel(point.period_start);
      title = monthLongLabel(point.period_start);
    }
    return { ...point, tick, title };
  });

/** `ชิ้นรายวัน · 1–9 ต.ค. 69`. */
export const seriesTitle = (series: BdcDashboardSeriesPoint[], granularity: BdcDashboardGranularity): string => {
  const unit = granularity === "day" ? "รายวัน" : granularity === "week" ? "รายสัปดาห์" : "รายเดือน";
  if (series.length === 0) return `ชิ้น${unit}`;
  return `ชิ้น${unit} · ${rangeLabel(series[0].period_start, series[series.length - 1].period_end)}`;
};

export type CellTone = "ordered" | "missed" | "lapsed" | "nofile";

/**
 * A day with no file for the store's DC is neutral, never a miss. A day the store skipped AFTER
 * its last order is `lapsed` — where it dropped off; earlier skips are part of its habit.
 */
export const cellTone = (cell: BdcDashboardStoreCell, lastOrderDate?: string): CellTone => {
  if (cell.ordered) return "ordered";
  if (!cell.expected) return "nofile";
  return lastOrderDate && cell.date > lastOrderDate ? "lapsed" : "missed";
};

const CELL_WHAT: Record<CellTone, string> = {
  ordered: "สั่ง",
  missed: "ไม่สั่ง",
  lapsed: "ไม่สั่ง (หลังครั้งล่าสุด)",
  nofile: "ไม่มีไฟล์ของ DC วันนั้น",
};

export const cellTitle = (cell: BdcDashboardStoreCell, lastOrderDate?: string): string =>
  `${weekdayDate(cell.date)} · ${CELL_WHAT[cellTone(cell, lastOrderDate)]}`;

/**
 * The calendar laid out Monday-first: leading nulls put the first day under its weekday, so each
 * row of 7 is one Mon–Sun week and a store's fixed order days line up as columns.
 */
export const mondayFirstCells = (cells: BdcDashboardStoreCell[]): (BdcDashboardStoreCell | null)[] => {
  const first = partsOf(cells[0]?.date);
  const lead = first ? (first.weekday + 6) % 7 : 0;
  return [...Array.from({ length: lead }, () => null), ...cells];
};

/** Monday-first, matching `order_weekdays` (0 = Monday). */
export const WEEKDAY_SHORT_MON_FIRST = ["จ", "อ", "พ", "พฤ", "ศ", "ส", "อา"];

const DAILY_ORDERS_PER_WEEK = 6;

/** The store's habit: `จ·พ·ศ`, `ทุกวัน` or `~3 วัน/สัปดาห์` (same wording as the Excel export). */
export const cadenceText = (row: Pick<BdcDashboardStoreRow, "order_weekdays" | "orders_per_week">): string => {
  if (row.order_weekdays.length > 0) return row.order_weekdays.map((day) => WEEKDAY_SHORT_MON_FIRST[day]).join("·");
  if (row.orders_per_week >= DAILY_ORDERS_PER_WEEK) return "ทุกวัน";
  return `~${Math.max(1, Math.round(row.orders_per_week))} วัน/สัปดาห์`;
};

export const LEVEL_LABEL: Record<BdcLapseLevel, string> = {
  ok: "ปกติ",
  thin: "ข้อมูลยังน้อย",
  late: "เลยรอบ",
  missed: "พลาดหลายรอบ",
  gone: "น่าจะเลิกสั่ง",
};

const LEVEL_TONE: Record<BdcLapseLevel, TagTone> = {
  ok: "ok",
  thin: "mut",
  late: "warn",
  missed: "high",
  gone: "crit",
};

/** The status tag of a store row: `⚠ พลาด ~3 รอบ`. */
export const levelTag = (row: Pick<BdcDashboardStoreRow, "level" | "expected_missed">): { tone: TagTone; text: string } => {
  const tone = LEVEL_TONE[row.level];
  if (row.level === "missed") return { tone, text: `⚠ พลาด ~${Math.round(row.expected_missed)} รอบ` };
  if (row.level === "late") return { tone, text: "⚠ เลยรอบ ~1 รอบ" };
  if (row.level === "gone") return { tone, text: `⚠ ${LEVEL_LABEL.gone}` };
  return { tone, text: LEVEL_LABEL[row.level] };
};

/** How a branch is named in filters and tables. */
export const branchLabel = (branch: BdcBranch | undefined, companyId?: string): string =>
  branch?.branch_code || branch?.branch_name || companyId || "-";

/** `pcs_per_order` shown with one decimal. */
export const avgText = (value: number | null | undefined) => formatNumber(value ?? 0, { decimals: 1 });

const FALLBACK_BY_STATUS: Record<number, string> = {
  403: "ไม่มีสิทธิ์ดูข้อมูล BDC นี้",
  422: "ยังไม่มีสาขาใดลงทะเบียนเป็นสาขา BDC",
};

/**
 * The message to show inline for a failed dashboard call. The server already speaks Thai
 * (BDC_FORBIDDEN / BDC_BRANCH_OUT_OF_SCOPE / BDC_COMPANY_NOT_CONFIGURED), so its message wins.
 */
export const dashboardErrorMessage = (error: unknown): string => {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } })?.response;
  const message = response?.data?.message;
  if (typeof message === "string" && message.trim()) return message;
  if (response?.status && FALLBACK_BY_STATUS[response.status]) return FALLBACK_BY_STATUS[response.status];
  if (!response) return "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาลองใหม่";
  return "โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่";
};
